import React from "react";
import DashboardView from "../DashboardView";
import "./SchedulePageLayout.css";

/**
 * The shell shared by every page in the Scheduled Reports section — list,
 * create / edit, logs. A page passes its name and its one action and renders
 * its own content; the app chrome, the page frame and the title block are
 * handled here, so none of them repeat that markup.
 *
 *   <SchedulePageLayout title="Scheduled Reports" actionLabel="New Schedule" onAction={…}>
 *     …page content…
 *   </SchedulePageLayout>
 *
 * `subtitle` defaults to today's date, which is what the design shows; pass a
 * string to say something else, or `null` for no second line. Pass no
 * `actionLabel` and the button is left off; pass `actions` to put something
 * else entirely up there.
 *
 * `frame` (default true) fixes the page to the viewport so the title block and
 * the app bar never scroll away. What moves underneath depends on the page:
 *
 *   frame                    — the content holds its own scroll region, as the
 *                              list does with the table
 *   frame + scrollBody       — the body scrolls itself, for a page that is just
 *                              tall, like the create / edit form
 *   frame={false}            — no fixed height at all; the window scrolls and
 *                              the title block goes with it
 */

const PlusIcon = (
  <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true">
    <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const todayLabel = () =>
  new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

export default function SchedulePageLayout({
  title,
  subtitle,
  actionLabel,
  actionIcon = PlusIcon,
  onAction,
  actionDisabled = false,
  actions,
  frame = true,
  scrollBody = false,
  className = "",
  children,
}) {
  // undefined means "the default"; null means "no second line at all"
  const secondLine = subtitle === undefined ? todayLabel() : subtitle;

  return (
    <DashboardView>
      <div
        className={[
          "spl",
          frame ? "spl--frame" : "spl--flow",
          frame && scrollBody ? "spl--scroll-body" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="spl-header">
          <div>
            <h1 className="spl-title">{title}</h1>
            {secondLine ? <p className="spl-subtitle">{secondLine}</p> : null}
          </div>
          {actions ||
            (actionLabel ? (
              <button type="button" className="spl-action" onClick={onAction} disabled={actionDisabled}>
                {actionIcon}
                {actionLabel}
              </button>
            ) : null)}
        </div>
        <div className="spl-body">{children}</div>
      </div>
    </DashboardView>
  );
}
